// ═══════════════════════════════════════════════════════════════════════════════
// CardReactions.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// The floating reaction bar rendered on the public eCard view (MoonbeamPreview).
// Shows the emojis the admin has assigned to this card's template, each with a
// running total of how many times it's been clicked.
//
// USAGE
//   <CardReactions
//     moonbeamUuid={uuid}                  // short uuid from the URL
//     emojis={reactionEmojis}              // [{ emojiid, emoji_char, display_name }]
//     viewerToken={viewerToken}            // ?r= or ?l= token, or null
//     viewerTokenType={viewerTokenType}    // 'recipient' | 'link' | null
//   />
//
// The parent (MoonbeamPreview) gets `emojis`, `viewerToken`, and
// `viewerTokenType` from the GET /moonbeams/view/:uuid response and just
// hands them down. This component is fully self-contained otherwise.
//
// BEHAVIOR
//   • On mount: fetches the current reaction summary (so the recipient sees
//     accumulated counts from earlier viewers — "❤️ 5" already there).
//   • Each button is independent. Clicking it:
//       1. Optimistically bumps its local count by 1 — feels instant.
//       2. Animates a "+1" floater above the button so the click feels alive.
//       3. POSTs to /reactions/:uuid. The server returns the fresh summary
//          which we use to authoritatively sync local state. This catches
//          drift if multiple people are reacting simultaneously.
//       4. On error, rolls the optimistic count back and shows a brief
//          unobtrusive message.
//   • No rate limiting on the client. Reactions are designed to accumulate;
//     a grandkid hitting ❤️ 20 times is the feature, not the bug. The server
//     accepts every click.
//   • No "you reacted" state. Anyone can click any emoji any number of times.
//     We deliberately don't try to remember "you already clicked this" —
//     that's a different feature with different semantics.
//
// LAYOUT
//   The bar floats at the bottom-center of the card iframe overlay (handled
//   by MoonbeamPreview's positioning). On mobile, the buttons are full-width
//   tappable targets sized for fingers. On desktop, they sit inline.
//
// SAFETY
//   • If `emojis` is empty or undefined, renders nothing. Lets the parent
//     pass through whatever the server returned without needing its own
//     conditional.
//   • If `moonbeamUuid` is missing, also renders nothing — defensive.
//   • Disabled state while a network call is in flight prevents accidental
//     double-counts when the user double-taps (which on mobile is common).
//     The next click waits for the previous summary to land.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import moonbeamService from '../../services/moonbeamService.js';

export default function CardReactions({
  moonbeamUuid,
  emojis = [],
  viewerToken = null,
  viewerTokenType = null,
}) {
  // counts: Map<emojiid, number>. Initialized empty and merged from the
  // fetched summary on mount. Using a plain object (not a Map) keeps React
  // reconciliation simple — every render gets a fresh reference.
  const [counts, setCounts] = useState({});

  // Pending in-flight reactions, keyed by emojiid. While a click is being
  // processed, the button shows a subtle press animation and is disabled
  // to prevent runaway double-clicks. Once the server responds (success
  // or error), the key is removed.
  const [pending, setPending] = useState({});

  // Floating "+1" animations, keyed by a monotonic id so multiple rapid
  // clicks each get their own floating element. Each entry: { id, emojiid }.
  // Cleared after the animation completes (700ms).
  const [floats, setFloats] = useState([]);
  const floatIdRef = useRef(0);

  const [errorMsg, setErrorMsg] = useState(null);
  const errorTimerRef = useRef(null);

  // ─── Fetch initial summary on mount ──────────────────────────────────────
  // We fetch even if the parent already has the summary structure, because
  // we need authoritative counts (other viewers may have reacted between
  // the original card-view fetch and now). Cheap call.
  useEffect(() => {
    if (!moonbeamUuid) return;
    let cancelled = false;

    moonbeamService.getReactionSummary(moonbeamUuid)
      .then((resp) => {
        if (cancelled) return;
        const totals = resp?.summary?.totals || [];
        const seeded = {};
        for (const t of totals) {
          seeded[t.emojiid] = t.count || 0;
        }
        setCounts(seeded);
      })
      .catch(() => {
        // Silent failure — buttons still work, they just start at 0.
        // The first successful click will replace counts with the
        // server's authoritative response.
      });

    return () => { cancelled = true; };
  }, [moonbeamUuid]);

  // ─── Auto-clear transient error toast ────────────────────────────────────
  useEffect(() => {
    if (!errorMsg) return;
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setErrorMsg(null), 3000);
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [errorMsg]);

  // ─── Click handler ───────────────────────────────────────────────────────
  const handleClick = useCallback(async (emojiid) => {
    if (!moonbeamUuid) return;
    if (pending[emojiid]) return;

    // 1) Optimistic bump
    setCounts(prev => ({ ...prev, [emojiid]: (prev[emojiid] || 0) + 1 }));

    // 2) Floating "+1"
    const floatId = ++floatIdRef.current;
    setFloats(prev => [...prev, { id: floatId, emojiid }]);
    setTimeout(() => {
      setFloats(prev => prev.filter(f => f.id !== floatId));
    }, 700);

    // 3) Server call
    setPending(prev => ({ ...prev, [emojiid]: true }));
    try {
      const resp = await moonbeamService.recordReaction(
        moonbeamUuid,
        emojiid,
        viewerToken,
        viewerTokenType
      );

      // Sync from authoritative summary if returned. The server's count
      // includes our just-recorded click plus anything anyone else has
      // submitted in parallel.
      const totals = resp?.summary?.totals;
      if (Array.isArray(totals)) {
        const fresh = {};
        for (const t of totals) {
          fresh[t.emojiid] = t.count || 0;
        }
        setCounts(fresh);
      }
    } catch (err) {
      // 4) Roll back the optimistic bump on failure.
      setCounts(prev => {
        const cur = prev[emojiid] || 0;
        return { ...prev, [emojiid]: Math.max(0, cur - 1) };
      });

      // Be specific when the server gave a useful reason; otherwise
      // generic. We don't want to scare the recipient.
      const msg = err?.data?.error;
      if (msg && /not available|disabled|expired/i.test(msg)) {
        setErrorMsg('Reactions aren\'t available on this card.');
      } else {
        setErrorMsg('Couldn\'t save your reaction. Try again.');
      }
    } finally {
      setPending(prev => {
        const next = { ...prev };
        delete next[emojiid];
        return next;
      });
    }
  }, [moonbeamUuid, viewerToken, viewerTokenType, pending]);

  // Empty / disabled — render nothing. Lets parent always include the
  // component without conditional wrappers.
  if (!moonbeamUuid || !emojis.length) return null;

  return (
    <>
      {/* Floating reaction bar. Parent positions this; we just lay out
          the buttons. Background uses a subtle pill so it reads on any
          card design.
       */}
      <div
        className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full bg-black/55 backdrop-blur-md border border-white/20 shadow-lg shadow-black/30"
        role="group"
        aria-label="Reactions"
      >
        {emojis.map((emoji) => {
          const count = counts[emoji.emojiid] || 0;
          const isPending = !!pending[emoji.emojiid];
          const floatsForEmoji = floats.filter(f => f.emojiid === emoji.emojiid);

          return (
            <div key={emoji.emojiid} className="relative">
              {/* Floating "+1" elements stacked above the button */}
              {floatsForEmoji.map((f) => (
                <span
                  key={f.id}
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 text-base font-bold text-gold"
                  style={{
                    animation: 'cardReactionFloat 700ms ease-out forwards',
                  }}
                  aria-hidden="true"
                >
                  +1
                </span>
              ))}

              <button
                type="button"
                onClick={() => handleClick(emoji.emojiid)}
                disabled={isPending}
                aria-label={`React with ${emoji.display_name}`}
                title={emoji.display_name}
                className={`
                  flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full
                  text-base sm:text-lg
                  bg-white/10 hover:bg-white/20 active:bg-white/30
                  border border-white/15 hover:border-white/30
                  text-white transition-all duration-150
                  ${isPending ? 'opacity-70 scale-95' : 'opacity-100 scale-100'}
                  focus:outline-none focus:ring-2 focus:ring-gold/60
                `}
              >
                <span className="leading-none" aria-hidden="true">{emoji.emoji_char}</span>
                {count > 0 && (
                  <span className="text-xs sm:text-sm font-medium tabular-nums min-w-[1ch]">
                    {count}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Transient error toast — only shown on failure. Sits in a fixed
          position above the bar so it doesn't push layout around. Parent
          positions the whole component; this toast layers itself relative
          to the viewport bottom.
       */}
      {errorMsg && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-3 py-2 bg-red-900/90 border border-red-700 rounded-lg text-red-100 text-sm shadow-lg backdrop-blur-sm"
          role="alert"
        >
          {errorMsg}
        </div>
      )}

      {/* Keyframes for the +1 floater. Kept inline so the component is
          fully self-contained — no extra global CSS needed. Tailwind's
          arbitrary animation syntax could express this, but @keyframes
          is clearer and easier to tune.
       */}
      <style>{`
        @keyframes cardReactionFloat {
          0%   { opacity: 0;  transform: translate(-50%, 0)   scale(0.9); }
          15%  { opacity: 1;  transform: translate(-50%, -4px) scale(1.05); }
          100% { opacity: 0;  transform: translate(-50%, -28px) scale(1); }
        }
      `}</style>
    </>
  );
}